import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  CreditCard, Briefcase, Car, Landmark, Home, GraduationCap, AlertTriangle,
  ExternalLink, Sparkles, Radio, Search, TrendingUp, ArrowRight
} from "lucide-react";

const CATEGORY_META = {
  personal_credit_cards: { label: "Personal Credit Cards", icon: CreditCard, accent: "amber" },
  business_credit_cards: { label: "Business Credit Cards", icon: Briefcase, accent: "sky" },
  auto_loans: { label: "Auto Loans", icon: Car, accent: "indigo" },
  business_funding: { label: "Business Funding", icon: Landmark, accent: "emerald" },
  mortgages: { label: "Mortgages", icon: Home, accent: "rose" },
  credit_education: { label: "Credit Education", icon: GraduationCap, accent: "violet" },
  alerts: { label: "Alerts", icon: AlertTriangle, accent: "red" },
};

const ACCENT_BG = {
  amber: "bg-amber-100 text-amber-700",
  sky: "bg-sky-100 text-sky-700",
  indigo: "bg-indigo-100 text-indigo-700",
  emerald: "bg-emerald-100 text-emerald-700",
  rose: "bg-rose-100 text-rose-700",
  violet: "bg-violet-100 text-violet-700",
  red: "bg-red-100 text-red-700",
};

export default function FundingFeed() {
  const [posts, setPosts] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [meta, setMeta] = useState({ categories: {}, disclaimer: "" });
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [reviewingPost, setReviewingPost] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 60 };
      if (category !== "all") params.category = category;
      if (q) params.q = q;
      const [feed, feat, m] = await Promise.all([
        api.get("/feed", { params }),
        api.get("/feed/featured"),
        api.get("/feed/meta"),
      ]);
      setPosts(feed.data);
      setFeatured(feat.data);
      setMeta(m.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const counts = useMemo(() => {
    const c = { all: posts.length };
    Object.keys(CATEGORY_META).forEach((k) => { c[k] = posts.filter((p) => p.category === k).length; });
    return c;
  }, [posts]);

  return (
    <AppShell>
      <div className="cq-in">
        {/* Live Intelligence header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 sm:p-10 mb-6 text-white">
          <div className="cq-grain absolute inset-0 opacity-30" />
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-amber-500/20 blur-3xl" />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-3 py-1 text-xs font-bold">
                <Radio size={14} className="text-emerald-400 animate-pulse" /> Live Credit Intelligence Feed
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-black mt-4">
                Your credit & funding <span className="text-amber-400">command center.</span>
              </h1>
              <p className="text-slate-300 mt-2 max-w-2xl text-lg">
                Real-time offers, alerts, and financial intelligence — curated and summarized by AI for people repairing credit, building business, and getting approved.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-300">
              <TrendingUp size={14} /> {posts.length} live signals
            </div>
          </div>
        </div>

        {/* Featured */}
        {featured && (
          <FeaturedCard post={featured} onBook={() => setReviewingPost(featured)} />
        )}

        {/* Search + filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-4 mt-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input placeholder="Search offers, alerts, education…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-10 h-11 rounded-xl bg-white border-slate-200" data-testid="feed-search-input" />
          </div>
          <Button variant="outline" onClick={load} className="h-11 rounded-xl" data-testid="feed-search-btn">Search</Button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="feed-filters">
          <FilterChip active={category === "all"} onClick={() => setCategory("all")} label="All" count={counts.all} icon={Sparkles} testid="filter-all" />
          {Object.entries(CATEGORY_META).map(([k, m]) => (
            <FilterChip key={k} active={category === k} onClick={() => setCategory(k)} label={m.label} count={counts[k] || 0} icon={m.icon} accent={m.accent} testid={`filter-${k}`} />
          ))}
        </div>

        {loading && <p className="text-slate-400 text-sm">Loading the feed…</p>}
        {!loading && posts.length === 0 && (
          <Card className="p-10 rounded-2xl border-slate-100 text-center" data-testid="feed-empty">
            <Sparkles className="mx-auto text-slate-300" size={40} />
            <p className="mt-3 text-slate-500">No posts yet in this category. Check back soon — new intelligence is pulled every hour.</p>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="feed-grid">
          {posts.filter((p) => !featured || p.id !== featured.id).map((p) => (
            <FeedCard key={p.id} post={p} onBook={() => setReviewingPost(p)} />
          ))}
        </div>

        {/* Disclaimer */}
        <div className="mt-10 rounded-xl bg-slate-100 border border-slate-200 p-4 text-xs text-slate-600" data-testid="feed-disclaimer">
          <p className="font-bold text-slate-700 mb-1">Disclaimer</p>
          <p>{meta.disclaimer}</p>
        </div>
      </div>

      <BookReviewDialog post={reviewingPost} onClose={() => setReviewingPost(null)} />
    </AppShell>
  );
}

function FilterChip({ active, onClick, label, count, icon: Icon, accent = "amber", testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold transition ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
      }`}
    >
      {Icon && <Icon size={14} className={active ? "text-amber-400" : `text-${accent}-600`} />}
      {label}
      <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{count}</span>
    </button>
  );
}

function CategoryBadge({ category }) {
  const m = CATEGORY_META[category] || CATEGORY_META.credit_education;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ACCENT_BG[m.accent]}`}>
      <Icon size={12} /> {m.label}
    </span>
  );
}

function FeaturedCard({ post, onBook }) {
  return (
    <Card className="relative overflow-hidden rounded-3xl border-slate-100 p-6 sm:p-8" data-testid="feed-featured-card">
      <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-amber-100 blur-2xl opacity-70" />
      <div className="relative">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 text-white px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest">
            <Sparkles size={12} /> Today's Best Opportunity
          </span>
          <CategoryBadge category={post.category} />
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-black text-slate-900 mt-3" data-testid="featured-title">{post.title}</h2>
        <p className="text-slate-700 mt-2 text-lg" data-testid="featured-summary">{post.summary}</p>
        {post.why_it_matters && (
          <p className="mt-3 text-sm text-slate-600"><span className="font-bold">Why it matters:</span> {post.why_it_matters}</p>
        )}
        {(post.recommended_for?.length || post.credit_score_range) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {post.credit_score_range && <Badge className="bg-emerald-100 text-emerald-800 rounded-full">Score {post.credit_score_range}</Badge>}
            {(post.recommended_for || []).map((t) => <Badge key={t} className="bg-slate-100 text-slate-800 rounded-full">{t}</Badge>)}
          </div>
        )}
        <div className="mt-6 flex flex-wrap gap-3">
          {post.button_link && (
            <a href={post.button_link} target="_blank" rel="noreferrer" className="inline-flex items-center bg-slate-900 hover:bg-slate-800 text-white h-11 px-5 rounded-xl font-bold text-sm" data-testid="featured-learn-more">
              {post.button_text || "Learn more"} <ExternalLink size={14} className="ml-1.5" />
            </a>
          )}
          <Button onClick={onBook} className="bg-amber-500 hover:bg-amber-600 h-11 rounded-xl" data-testid="featured-book-review">
            Book Funding Review <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

function FeedCard({ post, onBook }) {
  return (
    <Card className="p-5 rounded-2xl border-slate-100 flex flex-col h-full" data-testid={`feed-card-${post.id}`}>
      <div className="flex items-start justify-between gap-2">
        <CategoryBadge category={post.category} />
        <span className="text-[10px] text-slate-400 font-semibold">{fmtDate(post.published_at || post.created_at)}</span>
      </div>
      <h3 className="font-display text-lg font-bold text-slate-900 mt-3 leading-snug" data-testid={`card-title-${post.id}`}>{post.title}</h3>
      <p className="text-sm text-slate-600 mt-2 line-clamp-3">{post.summary}</p>
      {post.why_it_matters && (
        <p className="text-xs text-slate-500 mt-2"><span className="font-bold">Why it matters:</span> {post.why_it_matters}</p>
      )}
      {(post.recommended_for?.length > 0 || (post.tags?.length > 0)) && (
        <div className="mt-3 flex flex-wrap gap-1">
          {(post.recommended_for || []).slice(0, 3).map((t) => <Badge key={t} className="bg-amber-50 text-amber-800 border-amber-200 rounded-full text-[10px]" variant="outline">{t}</Badge>)}
          {(post.tags || []).slice(0, 3).map((t) => <Badge key={t} className="bg-slate-50 text-slate-600 rounded-full text-[10px]" variant="outline">{t}</Badge>)}
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
        <p className="text-xs text-slate-400 truncate flex-1">{post.source_name || "Credit Quack"}</p>
        <div className="flex gap-2">
          {post.button_link && (
            <a href={post.button_link} target="_blank" rel="noreferrer" className="inline-flex items-center text-sky-700 hover:text-sky-900 text-xs font-bold" data-testid={`learn-more-${post.id}`}>
              Learn more <ExternalLink size={12} className="ml-1" />
            </a>
          )}
          <Button size="sm" onClick={onBook} className="bg-amber-500 hover:bg-amber-600 h-8 text-xs" data-testid={`book-review-${post.id}`}>
            Book review
          </Button>
        </div>
      </div>
    </Card>
  );
}

function BookReviewDialog({ post, onClose }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/feed/book-review", { post_id: post?.id, note });
      toast.success("Request received! A specialist will reach out soon.");
      setNote("");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!post} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="book-review-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Book a Funding Review</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {post && (
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
              <p className="text-xs uppercase font-bold text-amber-700 tracking-wider">About this opportunity</p>
              <p className="text-sm font-bold text-slate-900 mt-1">{post.title}</p>
            </div>
          )}
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything the team should know? (Optional)" data-testid="book-review-note" />
          <p className="text-xs text-slate-500">A Credit Quack specialist will follow up in your dashboard chat.</p>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="book-review-submit">
            {busy ? "Sending…" : "Request review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; }
}
